'use client'

import { useState } from 'react'
import { TrashIcon, WarningIcon } from '@phosphor-icons/react'
import { Button } from './button'
import { Popover, PopoverContent, PopoverTrigger } from '@/ui/popover'
import { cn } from '@/lib/tailwind'

interface DeleteButtonProps {
  /** Click handler */
  onClick: (e: React.MouseEvent) => void
  /** Whether the delete action is in progress */
  isDeleting?: boolean
  /** Whether the button is disabled */
  disabled?: boolean
  /** Button variant: 'default' shows text, 'icon' shows only icon */
  variant?: 'default' | 'icon'
  /** Label text when not deleting (default: "Delete") */
  label?: string
  /** Label text when deleting (default: "Deleting...") */
  deletingLabel?: string
  /** Whether to show confirmation before deleting (default: true) */
  requireConfirmation?: boolean
  /** Confirmation message */
  confirmMessage?: string
  /** Additional className */
  className?: string
}

/**
 * Standardized delete button with consistent styling.
 * Shows destructive hover state, loading state, and optional confirmation popover.
 */
export function DeleteButton({
  onClick,
  isDeleting = false,
  disabled = false,
  variant = 'default',
  label = 'Delete',
  deletingLabel = 'Deleting...',
  requireConfirmation = true,
  confirmMessage = 'Delete this entry?',
  className,
}: DeleteButtonProps) {
  const [isOpen, setIsOpen] = useState(false)

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!requireConfirmation) {
      onClick(e)
      return
    }
    // Open confirmation popover
    setIsOpen(true)
  }

  const handleConfirm = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsOpen(false)
    onClick(e)
  }

  const handleCancel = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsOpen(false)
  }

  const buttonContent =
    variant === 'icon' ? (
      <Button
        variant="ghost"
        size="icon"
        onClick={handleClick}
        disabled={disabled || isDeleting}
        className={cn(
          'text-muted-foreground hover:text-destructive',
          className,
        )}
        aria-label={isDeleting ? deletingLabel : label}
      >
        <TrashIcon className="h-4 w-4" />
      </Button>
    ) : (
      <Button
        variant="ghost"
        size="sm"
        onClick={handleClick}
        disabled={disabled || isDeleting}
        className={cn(
          'text-muted-foreground hover:text-destructive',
          className,
        )}
      >
        <TrashIcon className="mr-2 h-4 w-4" />
        {isDeleting ? deletingLabel : label}
      </Button>
    )

  if (!requireConfirmation) {
    return buttonContent
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>{buttonContent}</PopoverTrigger>
      <PopoverContent
        className="w-auto p-3"
        align="end"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <WarningIcon className="text-destructive h-4 w-4" weight="fill" />
            <span className="text-sm font-medium">{confirmMessage}</span>
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              className="h-7 px-2 text-xs"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={handleConfirm}
              disabled={isDeleting}
              className="h-7 px-3 text-xs"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
